import { navigateToLegacyView, navigateToPortal } from '@/router/legacyNavigationBridge';
import { legacyNav, legacyWindow, legacyExternis, legacyModule, legacyContext, rawItems, refreshLegacyNav, refreshVisits, dismissNotification, setStorageSize, isLegacySynchronizing, safe } from './legacyNavState';
import { runWithVueDialogs } from './legacyDialogs';
import { getNotifications, markNotificationRead } from '@/api/communication/notification';
import { getReleaseNotes } from '@/api/core/releaseNotes';
import { updateUser } from '@/api/core/user';
import { logout as logoutSession } from '@/utils/auth/sessionService';
import { forgetSsoCustomer } from '@/utils/auth/ssoHandoff';
import { generateHelpjuiceJwt, replaceHelpjuiceLocale } from '@/utils/domain/helpjuice';
import { HOME_MENU } from '@/utils/domain/legacyMenuRules';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { confirmAction } from '@/composables/useConfirmAction';
import { openChromeDialog } from '@/composables/useChromeDialogs';
import { useTranslator } from '@/composables/useTranslator';
import { log } from '@/utils/core/vueLogger';
import { isOnline } from '@/sync/connectivity';
import { toggleOffline } from './legacyConnectivity';

// Actions of the Vue chrome, which replaced the legacy nav / sidebar components and the ones they
// hosted (notification, visit preview, cart preview) - those files no longer exist. Every action
// calls the legacy business module the removed element used to call (sfaHelper, shoppingCart,
// salesHelper, storage, waSupport...) so offline rules and side effects stay exactly the legacy ones,
// while Vue owns every confirmation (confirmAction / NavConfirmDialog) instead of a legacy swal:
//   - logout()/logoutForLanguage(): nav.js logout() - a per-token POST + storage/cookie cleanup +
//     ctx.user reset. The legacy keeps its own copy (sessionHelper.logout) for its 401 handler.
//   - openHelp()/openAllReleaseNotes(): the Helpjuice HMAC JWT (helpjuice.js) only needs
//     externis.helpjuiceSecret, itself served in plaintext to the browser.
//   - savePassword()/saveLanguage(): nav.js saveUser()'s `tokenApp || token` header is always plain
//     `token` (tokenApp is never assigned in public/m.eagle) - the token authenticatedFetch() sends.
//   - the support actions: a confirm + a bare call to a waSupport module method.
//   - showCartLineDetail: cartPreview.js showDetail() was a lookup + `amplify.publish`.
//   - the visits actions (openVisit/startVisit/reloadVisits/cancelVisit/cancelLateVisits): ported
//     from visitPreview.js against @/utils/domain/legacyVisitRules.js (see legacyNavState.js's
//     refreshVisits() for the side effects visitPreview.js init() performed).

const t = useTranslator();

export const HELP_URL = 'https://ecos.helpjuice.com/fr_FR/ecos-sales';

// Grace periods covering the timers and XHR callbacks that report the result (swal / toast):
// saving a visit, cancelling it, saving the user, reading a notification… Support backups upload.
const ACTION_HOLD_MS = 20000;
const SUPPORT_HOLD_MS = 60000;

function call(label, fn, holdMs = ACTION_HOLD_MS) {
	try {
		return runWithVueDialogs(legacyWindow(), fn, { holdMs });
	} catch (error) {
		log('error', 'legacyNav', `${label} failed: ${error.message}`);
		return undefined;
	}
}

// #region Navigation

export function openMenuItem(item) {
	if (item?.shortcut) return openSalesShortcut(item.shortcut);
	if (item?.payload) return navigateToLegacyView(item.payload);
	return false;
}

// The draft shortcut opens NavDraftCartsDialog (AppChrome). vwSalesEdito.js clickOfflineHandler()/
// clickOfflineErrorHandler() are pure one-line navigations: inlined directly, no vmEdito dependency.
const OFFLINE_SHORTCUT_DATA = { offline: 'OFFLINE-ORDERS', offlineErrors: 'OFFLINE-ORDERS-ERRORS' };
export function openSalesShortcut(key) {
	const data = OFFLINE_SHORTCUT_DATA[key];
	return data ? navigateToLegacyView({ appMenu: 'menuSALES', view: 'vwSalesCartFormValidation', data }) : false;
}

// shoppingCart.js getDraftList() (drafts older than 31 days are purged by it).
export function readDraftCarts() {
	return new Promise((resolve) => {
		const cart = cartModule();
		if (!cart?.getDraftList) return resolve([]);
		call('getDraftList', () => cart.getDraftList((list) => resolve((list ?? []).map(({ id, title, modifiedDate, nbPdts }) => ({ id, title: title ?? '', modifiedDate: modifiedDate ? new Date(modifiedDate).getTime() : null, nbPdts: nbPdts ?? 0 })))));
	});
}

// vwSalesEdito.js loadDraft(): the cart form opens once the draft is restored from storage.
export function loadDraftCart(id) {
	call('loadDraft', () => cartModule().loadDraft(id));
	setTimeout(() => navigateToLegacyView({ appMenu: 'menuSALES', view: 'vwSalesCartForm' }), 250);
}

export const removeDraftCart = (id) => call('removeDraft', () => cartModule().removeDraft(id));

// The legacy re-evaluated the draft counter when its modal closed (vwSalesEdito.js checkDraftCart()):
// the chrome's own counter comes from the same shoppingCart.getDraftList() (refreshShortcuts).
export const refreshDraftCounter = () => refreshLegacyNav();

export const openPlanning = () => navigateToLegacyView({ appMenu: 'menuSFA', view: 'vwSFAPlanning' });

// The portal is a Vue page: the application keeps running behind it (formerly c_reloadWebApp, a full reload).
export const openPortal = () => navigateToPortal();

const openHomeView = () => navigateToLegacyView({ appMenu: HOME_MENU.appMenu, view: HOME_MENU.view, label: HOME_MENU.label });

// The widgets configuration belongs to the home dashboard (WidgetLayout, vue-widgets-zone, mounted on the home
// view only): the home view opens, and WidgetLayout opens the dialog once its widgets are loaded.
export function openWidgetSettings() {
	openHomeView();
	openChromeDialog('widgetSettings');
}

// vwWidgets.js widgetSettingsHandler(): the legacy (Knockout) widgets list. Its VM (externis.sales.vwWidgetsVM)
// and its modal (#mdWidgetsList, widgetsList.js, listening once rendered) only live on the home view.
const LEGACY_WIDGETS_RETRY_MS = 200;
const LEGACY_WIDGETS_ATTEMPTS = 50;

export function openLegacyWidgetSettings() {
	openHomeView();
	const open = (attempt = 0) => {
		const vm = safe(() => legacyExternis().sales.vwWidgetsVM);
		if (vm && safe(() => legacyWindow().document.getElementById('mdWidgetsList'))) return call('widgetSettingsHandler', () => vm.widgetSettingsHandler());
		if (attempt < LEGACY_WIDGETS_ATTEMPTS) setTimeout(open, LEGACY_WIDGETS_RETRY_MS, attempt + 1);
		else log('warn', 'legacyNav', 'Legacy widgets view unavailable - widgets list not opened');
	};
	open();
}

// PointsDialogZone (#22908, persistent, mounted after the main synchronization) lists the whole history
// when no offer filter comes with the event.
export const openPointsHistory = () => window.dispatchEvent(new CustomEvent('open-vue-points-dialog'));

// #endregion

// #region Session & connection

export { toggleOffline };

// nav.js synchronize(): an offline info swal, else a bare `amplify.publish('evtManualSynch')` - no
// VM state involved, called directly.
export function synchronize() {
	if (!isOnline()) {
		confirmAction({ title: t('OfflineMode'), lines: [t('OfflineOnlyAction')], icon: 'wifi_off', confirmText: 'OK', cancelText: null, run: () => {} });
		return;
	}
	call('synchronize', () => legacyWindow().amplify.publish('evtManualSynch'));
}

// Startup.js updateReadyToInstall(), owned by Vue: the first load after a deployment still runs the previous legacy
// from its service worker cache, whose swal would open in the hidden iframe of the login page or the portal. Its
// APPLICATIONRESOURCES purge went with the legacy translations: nothing reads that key, the Vue overlay
// (src/api/core/resource.js) revalidates on every load.
export function installUpdate() {
	if (!isOnline()) {
		confirmAction({ title: t('OfflineMode'), lines: [t('OfflineOnlyAction')], icon: 'wifi_off', confirmText: 'OK', cancelText: null, run: () => {} });
		return;
	}
	confirmAction({
		title: t('New Update'),
		lines: [t('New version available. Please refresh the page.')],
		icon: 'system_update',
		confirmText: 'OK',
		cancelText: t('UpdateLater'),
		run: () => window.location.reload(),
	});
}

// nav.js updateHandler(): swal + reload on confirm - called directly, no VM dependency left.
// The document must come back blank (legacySession.js "never enter #ecos any other way"): reloading at
// the current #ecos hash re-opens straight into the application before Vue re-primes the session, which
// is what left the home menu broken until a manual top-level refresh (that one recreates the iframe from
// its hash-less src). Clearing the hash first makes this reload boot blank, same as a fresh iframe load.
export function installExtensionUpdate() {
	confirmAction({
		title: t('New Update'),
		lines: [t('NewUpdateMsg')],
		icon: 'extension',
		confirmText: t('Confirm'),
		cancelText: t('Cancel'),
		run: () => window.location.reload(),
	});
}

// nav.js clearExternisAppCachesFromWindow(): plain Cache API, no VM state.
function clearExternisAppCaches() {
	const win = legacyWindow();
	if (!win || !('caches' in win)) return Promise.reject(new Error('CacheStorage not available.'));
	return win.caches.keys().then((names) => {
		const externisCaches = names.filter((name) => name.indexOf('externis.appcache') === 0);
		return Promise.all(externisCaches.map((name) => win.caches.delete(name))).then(() => ({ ok: true, deletedCaches: externisCaches }));
	});
}

// nav.js sendMessageToServiceWorker(): plain Service Worker API, no VM state - called directly
// against the iframe window instead of through vmNav().clearMEagleCacheHandler().
function clearMEagleServiceWorkerCache() {
	const win = legacyWindow();
	if (!win || !('serviceWorker' in win.navigator)) return clearExternisAppCaches();
	return win.navigator.serviceWorker.getRegistration().then((registration) => {
		const targetWorker = win.navigator.serviceWorker.controller || registration?.active || registration?.waiting;
		if (!targetWorker) return clearExternisAppCaches();
		return new Promise((resolve, reject) => {
			const channel = new win.MessageChannel();
			const timer = setTimeout(() => reject(new Error('The service worker is not responding.')), 5000);
			channel.port1.onmessage = (event) => {
				clearTimeout(timer);
				if (event.data?.ok) resolve(event.data);
				else reject(new Error(event.data?.error || 'Unable to clear the m.eagle cache.'));
			};
			targetWorker.postMessage({ type: 'CLEAR_M_EAGLE_CACHE' }, [channel.port2]);
		});
	});
}

// nav.js clearMEagleCacheHandler(): a no-op unless isLocalhost() - a dev-only tool. Reloads
// window.parent (nav.js runs inside the iframe, so from Vue's own code that's just `window`).
export function clearLegacyAppCache() {
	if (!legacyNav.isLocalhost) return;
	call('clearMEagleCacheHandler', () =>
		clearMEagleServiceWorkerCache()
			.then(() => window.location.reload(true))
			.catch((error) => log('error', 'legacyNav', `clearMEagleCacheHandler failed: ${error.message}`))
	);
}

// nav.js getHelpjuiceUrl(): HMAC JWT signed with externis.helpjuiceSecret (@/utils/domain/helpjuice.js) -
// already served in plaintext to the browser, not a secret guarded by the VM. The HTML spec extends a
// user activation to same-origin descendant frames, so the window.open() that follows isn't a popup.
export async function openHelp(url = HELP_URL) {
	const secret = safe(() => legacyExternis().helpjuiceSecret);
	const locale = safe(() => legacyWindow().getLocalLanguage(), 'fr-FR') || 'fr-FR';
	const jwt = await generateHelpjuiceJwt('logiciels@externis.com', secret);
	const fallbackUrl = replaceHelpjuiceLocale(url, locale);
	call('help', () => legacyWindow().open(`https://helpjuice.com/jwt/ecos?jwt=${encodeURIComponent(jwt)}&fallback_url=${encodeURIComponent(fallbackUrl)}`, '_blank'));
}

// nav.js logout(): the Vue session's (sessionService.js) - every token invalidated, including the ones the
// application re-logged on with (eagle.js logUser), the remembered session forgotten, then the login page.
function performLogout() {
	const user = safe(() => legacyContext().user, {}) ?? {};
	return logoutSession({ extraTokens: [user.token, user.tokenSales, user.tokenDoc, user.tokenAssort].filter(Boolean) });
}

// Leaving during a synchronization would cut it short (synchSFA uploads the visits saved offline): the user
// logs out only once it is over. The chrome disables its logout entries meanwhile, this covers the remaining paths.
function blockedBySynchronization() {
	if (!isLegacySynchronizing()) return false;
	confirmAction({ title: t('Synchronization'), lines: [t('PleaseWaitSync')], icon: 'sync', confirmText: 'OK', cancelText: null, run: () => {} });
	return true;
}

export function logout() {
	if (blockedBySynchronization()) return false;
	// A delegated session runs in #ifm-delegation of the delegating user's legacy app (salesHelper.connectAs):
	// "logout" closes that frame, as disableUserMenu() (js/Main.js) rewires the legacy menu to do.
	if (legacyNav.delegation) return call('logoutAs', () => window.parent.externis.sales.salesHelper.logoutAs());
	return performLogout();
}

// #endregion

// #region Notifications

// notification.js readNotification()'s type===3 dispatch (OrderLine tracking / DCRFO document
// download / SALFO geoloc bypass) and type===1 export download - called directly on the legacy
// globals it already used, never through vmNotification.
function dispatchNotification(item) {
	const ext = legacyExternis();
	const win = legacyWindow();

	if (item.type === 1) {
		safe(() => ext.fileHelper.downloadFile(item.data, item.title));
		return;
	}
	if (item.type !== 3) return;

	if (item.url && item.url.startsWith('OrderLine;') && item.url.split(';')[1] === 'OrderLineId') {
		const orderLineId = parseInt(item.url.split(';')[2], 10);
		// HACK (notification.js): force a refresh if the tracking view is already the current one.
		if (safe(() => ext.ecosVM.currentView()) === 'vwOrdersTracking') safe(() => win.amplify.publish('evtChangeView', { appMenu: 'menuSALES', view: 'vwSalesEdito' }));
		setTimeout(() => safe(() => win.amplify.publish('evtChangeView', { appMenu: 'menuSALES', view: 'vwOrdersTracking', data: { orderLineId } })), 750);
	}

	if (item.application === 'DCRFO' && item.exportId) {
		const doc = safe(() => ext.ecosVM.documents().find((d) => d.id === item.exportId));
		const version = doc?.versions?.[0];
		if (doc) safe(() => ext.doc.docHelper.downloadFile(`${ext.doc.docHelper.pxKeyFile}${doc.id}`, version?.fileName, version?.chunkSize, version?.totalChunks));
	}

	if (item.application === 'SALFO' && item.url?.startsWith('BypassGeoloc;')) {
		const code = item.url.split(';')[1].trim();
		const helper = sfaHelper();
		if (safe(() => helper.decodeGeoCode(code))) helper.evalGeoCode(code);
	}
}

// notification.js getNotifications(): document.visibilityState/hiddenApp/isOnline guards are the
// legacy VM's own polling concerns, not ours - a manual reload always forces a real refetch.
export const reloadNotifications = () => call('getNotifications', () => getNotifications(true).then(refreshLegacyNav));

// notification.js clearAllNotifications(): an info swal while offline, otherwise a confirm, then
// markAsRead() for every listed item - markNotificationRead() (Communication/.../ChangeState) for each.
export function clearNotifications() {
	if (!isOnline()) {
		confirmAction({ title: t('NotifOffline'), icon: 'wifi_off', confirmText: 'OK', cancelText: null, run: () => {} });
		return;
	}
	confirmAction({
		title: t('Are you sure?'),
		lines: [t('Mark all notifications as read')],
		icon: 'done_all',
		confirmText: t('Yes'),
		cancelText: t('Cancel'),
		run: () => call('clearAllNotifications', () => Promise.all(legacyNav.notifications.map((n) => markNotificationRead(n.id, n.application).then(() => dismissNotification(n.id))))),
	});
}

// The legacy row "delete" icon both acts on the notification (export download, order line, geoloc
// bypass code) and marks it as read.
export function readNotification(id) {
	const item = rawItems.notifications.get(id);
	if (!item) return;
	if (!isOnline()) {
		confirmAction({ title: t('NotifOffline'), icon: 'wifi_off', confirmText: 'OK', cancelText: null, run: () => {} });
		return;
	}
	call('readNotification', () => dispatchNotification(item));
	call('markNotificationRead', () => markNotificationRead(item.id, item.application).then(() => dismissNotification(item.id)));
}

// notification.js buildNotificationList(): a document notification opens that document.
export function openNotification(id) {
	const ext = legacyExternis();
	const item = rawItems.notifications.get(id);
	if (item?.application !== 'DCRFO') return false;

	return call('openDocument', () => {
		const files = ext.ecosVM.documents().filter((d) => d.label == item.data);
		const file = files[files.length - 1];
		if (!file) {
			log('warn', 'legacyNav', `notification: no local document matching "${item.data}"`);
			return false;
		}
		const params = { selector: 'body', obj: ext.doc.docHelper.getUniqueId(file) };
		legacyWindow().amplify.publish('evtDocument', legacyWindow().btoa(JSON.stringify(params)));
		return true;
	});
}

// #endregion

// #region Visits

// visitPreview.js: ported against @/utils/domain/legacyVisitRules.js (legacyNavState.js's
// refreshVisits() and its 3 side effects) instead of reading vmVisitPreview - see the file header.
const sfaHelper = () => legacyModule('sfaHelper');
const STATUS = iframeDataBridge.enumAppointment.status;

// visitPreview.js refreshHandler(): a fresh classification pass + ecosVM.setAccountVisitLogos(),
// shared by reloadVisits() and cancelVisit()'s own callback (its waiterHelper block targeted the
// removed legacy panel).
function runRefreshHandler() {
	refreshVisits();
	setTimeout(() => safe(() => legacyExternis().ecosVM.setAccountVisitLogos()), 2500);
}

function goToLiveVisit(appointment) {
	const win = legacyWindow();
	if (safe(() => legacyExternis().ecosVM.currentView()) === 'vw-visit') {
		safe(() => win.amplify.publish('evtChangeView', { view: 'greeter' }));
		setTimeout(() => safe(() => win.amplify.publish('evtChangeView', { appMenu: 'menuSFA', view: 'vw-visit', data: { appointment } })), 2500);
	} else {
		safe(() => win.amplify.publish('evtChangeView', { appMenu: 'menuSFA', view: 'vw-visit', data: { appointment } }));
	}
}

// visitPreview.js gotoVisit(): a late+confirmed visit is auto-started (sfaHelper.saveAppointment)
// before the view change - the legacy's mobile/desktop dropdown-closing DOM tweaks are omitted,
// Vue already closes its own panel.
export function openVisit(key) {
	const item = rawItems.visits.get(key);
	if (!item) return;
	call('gotoVisit', () => {
		const appointment = item.appointment;
		if (key.startsWith('late:') && appointment.status === STATUS.CONFIRMED) {
			appointment.status = STATUS.INPROGRESS;
			sfaHelper().saveAppointment(null, appointment, () => goToLiveVisit(appointment));
			return;
		}
		goToLiveVisit(appointment);
	});
}

export const startVisit = (key) => {
	const item = rawItems.visits.get(key);
	if (item) call('start', () => legacyWindow().amplify.publish('evtAppointment', { appointment: item.appointment }));
};

// visitPreview.js cancel(): swal + sfaHelper.cancelAppointment(appointment, callback), the callback
// refreshes the panel, publishes evtAgendaChange, and redirects if the cancelled visit is open.
export function cancelVisit(key) {
	const item = rawItems.visits.get(key);
	if (!item) return;
	confirmAction({
		title: t('CancelAppointment?'),
		lines: [t('CancelAppointmentConfirm')],
		icon: 'cancel',
		confirmText: t('Yes'),
		cancelText: t('No'),
		run: () =>
			call('cancelAppointment', () =>
				sfaHelper().cancelAppointment(item.appointment, () => {
					const win = legacyWindow();
					const ext = legacyExternis();
					setTimeout(() => call('refreshHandler', runRefreshHandler), 500);
					safe(() => win.amplify.publish('evtAgendaChange', { forceRefresh: true }));
					if (safe(() => ext.ecosVM.currentView()) === 'vw-visit' && safe(() => ext.sales.vwVisit.currentAppointment().uid) === item.appointment.uid) {
						safe(() => win.amplify.publish('evtChangeView', { appMenu: 'menuSFA', view: 'vwSFAPlanning' }));
					}
				})
			),
	});
}

// visitPreview.js codeHandler(): swal(input) + sfaHelper.evalGeoCode(value).
export function enterBypassCode() {
	confirmAction({
		title: t('GeolocTitleBypassCode'),
		inputLabel: t('GeolocAskBypassCode'),
		confirmText: 'OK', // codeHandler's swal never set confirmButtonText: sweetalert v1's own untranslated default.
		cancelText: t('Cancel'),
		run: (value) => value && call('evalGeoCode', () => sfaHelper().evalGeoCode(value)),
	});
}

// visitPreview.js cancelLates(): recursive drain of SFA_APPOINTMENTS_LATE, one sfaHelper.cancelAppointment
// at a time (sequential storage writes) - ported directly, no vmVisitPreview involved.
function cancelNextLateVisit() {
	const storage = legacyModule('storage');
	safe(() =>
		storage.get('SFA_APPOINTMENTS_LATE', (lates) => {
			if (!legacyWindow()) return;
			const cancellable = (lates ?? []).filter((item) => [STATUS.SCHEDULED, STATUS.CONFIRMED].includes(item.status));
			const appointment = cancellable.pop();
			if (!appointment) {
				safe(() => legacyWindow().amplify.publish('evtAgendaChange', { forceRefresh: true }));
				setTimeout(runRefreshHandler, 150);
				return;
			}
			const remaining = (lates ?? []).filter((item) => item !== appointment);
			sfaHelper().cancelAppointment(appointment, () => storage.save('SFA_APPOINTMENTS_LATE', remaining, false, cancelNextLateVisit));
		})
	);
}

// visitPreview.js cancelLatesHandler(): swal + self.cancelLates() - only its confirmation swal is replaced.
export function cancelLateVisits() {
	confirmAction({
		title: t('CancelAllLatesConfirmation'),
		icon: 'cancel',
		confirmText: t('Yes'),
		cancelText: t('No'),
		run: () => call('cancelLates', cancelNextLateVisit),
	});
}

export const reloadVisits = () => call('refreshHandler', runRefreshHandler);

// #endregion

// #region Cart

const cartModule = () => legacyModule('shoppingCart');

// cartPreview.js goToCheckout(): a large cart's render is a long task - show the legacy loader first
// so it's actually painted before triggering it. Never showed a dialog: no VM dependency to remove
// beyond the navigation itself, already router-driven like every other menu/shortcut entry.
export function goToCheckout() {
	const go = () => navigateToLegacyView({ appMenu: 'menuSALES', view: 'vwSalesCartForm' });
	// cartPreview.js itemsCount = shoppingItems.length + shoppingItemsRSW.length + shoppingItemsAssort.length,
	// the same partition legacyNav.cart.count already carries (groupCartProducts(), legacyNavModel.js).
	const itemsCount = legacyNav.cart.count;
	const greeter = safe(() => legacyExternis().greeterVM);
	if (itemsCount > 8 && greeter) {
		call('startLoading', () => greeter.startLoading());
		setTimeout(go, 30);
	} else {
		go();
	}
}

// cartPreview.js clearShoppingCart(): swal + cart.clearCart(), which already releases the locally
// reserved stock/quota by itself - called directly, only its confirmation swal is replaced.
export function clearCart() {
	confirmAction({
		title: t('Are you sure?'),
		lines: [t('The shopping cart will be cleared!')],
		icon: 'delete_sweep',
		confirmText: t('Yes'),
		cancelText: t('Cancel'),
		run: () => call('clearCart', () => cartModule().clearCart()),
	});
}

// cartPreview.js showDetail(): lookups on ecosVM's plain arrays + amplify.publish - no VM read. The
// productdetail-modal itself is registered globally (app.js), never mounted from nav.htm/sidebar.htm.
export function showCartLineDetail(key) {
	const product = rawItems.cart.get(key);
	if (!product) return false;
	const ext = legacyExternis();
	const find = (list, id) => (safe(() => list, []) ?? []).find((item) => item.id === id);
	const params = {
		id: product.id,
		rsw: find(
			safe(() => ext.ecosVM.rsws),
			product.rswId
		),
		orderType: product.orderType,
		dl: find(
			safe(() => ext.ecosVM.deliveryLocations),
			product.apDlId
		),
		company: find(
			safe(() => ext.ecosVM.companies),
			product.apCompanyId
		),
		catalog: find(
			safe(() => ext.ecosVM.catalogs),
			product.formDataInfos?.catalogId || product.formData?.catalogId || product.catalogId
		),
		campaign: find(
			safe(() => ext.ecosVM.campaigns),
			product.campaignId
		),
	};
	return call('showDetail', () => legacyWindow().amplify.publish('evtModalProductDetail', params));
}
// cartPreview.js zoomTn(): full image online, synchronized thumbnail offline.
export function loadCartLineImage(key) {
	const product = rawItems.cart.get(key);
	if (!product?.imageUid) return Promise.resolve(null);
	return loadProductThumb(isOnline() ? product.imageUid : product.imageThumbnailUid, product.process);
}

// cartPreview.js loadCompanyDLHandler(): pure navigation, never showed a dialog - inlined directly,
// location.key is already the "companyId.dlId" pair (legacyNavModel.js groupCartProducts()).
export function openAssortLocation(location) {
	if (safe(() => legacyExternis().ecosVM.currentView()) === 'vwSalesCatalog') {
		call('evtLoadFilters', () => legacyWindow().amplify.publish('evtLoadFilters', { companyId: location.companyId, dlId: location.dlId }));
	} else {
		navigateToLegacyView({ appMenu: 'menuSALES', view: 'vwSalesCatalog', data: { companyId: location.companyId, dlId: location.dlId } });
	}
}

// cartPreview.js deleteProductsByDeliveryLocation(): swal + cart.removeProduct(ids) filtered by
// apDlId - read straight from cart.getProducts(), no need for vmCartPreview.init()'s own list first.
export function clearAssortLocation(location) {
	confirmAction({
		title: t('Are you sure?'),
		lines: [t('The product will be removed from the shopping cart!')],
		icon: 'delete',
		confirmText: t('Yes, delete it!'),
		cancelText: t('Cancel'),
		run: () =>
			call('removeProduct', () => {
				const cart = cartModule();
				const ids = (safe(() => cart.getProducts(), []) ?? []).filter((p) => p.apDlId === location.dlId).map((p) => p.id);
				cart.removeProduct(null, null, null, null, null, null, ids);
			}),
	});
}

export function removeCartLine(key) {
	const p = rawItems.cart.get(key);
	if (p) call('removeProduct', () => cartModule().removeProduct(p.id, p.campaignId, p.rswId, p.apDlId, p.apCompanyId, p.apIsFree, null, p._isDateFilling, p._dateFillingIndex));
}

// cartPreview.js updateQtyHandler() without its DOM event (it reads the clicked icon's text).
export function changeCartQuantity(key, quantity) {
	const cartProd = rawItems.cart.get(key);
	const ecosVM = safe(() => legacyExternis().ecosVM);
	if (!cartProd || !ecosVM) return;

	const byId = (list) => safe(() => list.find((product) => product.id === cartProd.id));
	const product = byId(safe(() => ecosVM.products(), [])) || byId(ecosVM.missingRSWProducts) || byId(ecosVM.productsAssort);

	call('addProduct', () =>
		cartModule().addProduct({
			product,
			quantity,
			campaignId: cartProd.campaignId,
			dlId: cartProd.dlId,
			rswId: cartProd.rswId,
			apCompanyId: cartProd.apCompanyId,
			apDlId: cartProd.apDlId,
			apIsFree: cartProd.apIsFree,
			silentMode: true,
			orderType: cartProd.orderType,
			containerId: cartProd.formData?.containerId || cartProd.formDataInfos?.containerId || 0,
			catalogId: cartProd.formData?.catalogId || cartProd.formDataInfos?.catalogId || 0,
		})
	);
}

// salesHelper.getProductThumb() already falls back to the thumbnails synchronized for offline use.
export function loadProductThumb(uid, process) {
	const fallback = `/m.eagle/img/catalog/process${process}_thumb.png`;
	const salesHelper = legacyModule('salesHelper');
	if (!salesHelper?.getProductThumb) return Promise.resolve(fallback);

	return new Promise((resolve) => {
		try {
			salesHelper.getProductThumb(uid, { key: uid, callback: (key, url) => resolve(url || fallback) });
		} catch {
			resolve(fallback);
		}
	});
}

// #endregion

// #region Profile

const salesHelper = () => legacyModule('salesHelper');

export function readProfile() {
	const ext = legacyExternis();
	const ctx = legacyContext();
	const win = legacyWindow();
	const enabled = safe(() => salesHelper().userExtensions.getList(), []) ?? [];

	return {
		languages: (safe(() => ext.languages, []) ?? []).map(({ id, label }) => ({ id, label })),
		language: safe(() => ctx.user.locale, null) || safe(() => win.getLocalLanguage(), 'fr-FR'),
		version: `v${legacyNav.version}`,
		allowDocSync: !safe(() => ext.disableDocSync, false),
		agenda: {
			active: Boolean(safe(() => ext.msal.isActive, false)),
			calendars: (safe(() => ext.msal.calendars, []) ?? []).map((c) => ({ id: c.id, name: c.name, isDefault: Boolean(c.isDefaultCalendar) })),
			activated: [...(safe(() => ext.msal.activatedCalendars, []) ?? [])],
		},
		extensions: (safe(() => ext.ecosVM.userExtensions, []) ?? []).map((ue) => ({
			extensionId: ue.extensionId,
			reference: ue._reference,
			mandatory: Boolean(ue._isMandatory),
			hasSettings: Boolean(ue._options?.settings),
			enabled: Boolean(ue._isMandatory) || enabled.some((item) => item.extensionId === ue.extensionId),
		})),
	};
}

// nav.js saveUserLanguage(): the new locale only applies after a logout / login - fire-and-forget the
// save, exactly like the legacy (it doesn't await its own AJAX call either before returning true).
export function saveLanguage(locale) {
	const ctx = legacyContext();
	if (!ctx?.user || !locale) return false;
	ctx.user.locale = locale;
	legacyWindow().uiLang = locale;
	call('saveUser', () =>
		updateUser(ctx.user, '').then((result) => {
			if (!result.ok) return;
			showSaveSuccess();
			safe(() => legacyModule('storage').save('USER', ctx.user));
		})
	);
	return true;
}

export function logoutForLanguage(locale) {
	if (blockedBySynchronization()) return false;
	safe(() => legacyWindow().localStorage.setItem('loginLocale', locale));
	return performLogout();
}

// nav.js saveUserPassword(). Returns the translation key of the validation error, or null once sent.
const PASSWORD_ERROR_KEYS = new Set(['ERR-0092', 'ERR-0093', 'ERR-0094']);

export async function savePassword({ current = '', next = '', confirm = '' }) {
	const ctx = legacyContext();
	const helper = salesHelper();
	if (!current) return 'CheckOldPass';
	if (!next || !confirm) return 'CheckNewPassEmpty';
	if (next !== confirm) return 'CheckNewPass';
	if (!safe(() => helper.crypt.compare(current, ctx.user.pass), false)) return 'CheckOldPass';
	if (next === current) return 'CheckPassDifferent';

	// saveUser() restores the old password when the server rejects the new one (complexity, history).
	const oldPass = ctx.user.pass;
	ctx.user.pass = helper.crypt.hash(next);

	const result = await call('saveUser', () => updateUser(ctx.user, next));
	if (result?.ok) {
		showSaveSuccess();
		safe(() => legacyModule('storage').save('USER', ctx.user));
		return null;
	}

	ctx.user.pass = oldPass;
	return PASSWORD_ERROR_KEYS.has(result?.code) ? result.code : 'An error occured';
}

export const agendaSignIn = () => call('msSignIn', () => legacyWindow().agendaSynch.msSignIn());

// nav.js resetExternalHandler(): swal + sfaHelper.deleteExternalAppointments() - called directly.
export function resetExternalAgenda() {
	confirmAction({
		title: t('Are you sure?'),
		lines: [t('ResetEXT_Confirm')],
		icon: 'sync_disabled',
		confirmText: t('Yes'),
		cancelText: t('Cancel'),
		run: () => call('deleteExternalAppointments', () => sfaHelper().deleteExternalAppointments()),
	});
}

// nav.js saveUserAgenda().
export function saveAgenda({ active, calendars }) {
	const ext = legacyExternis();
	return call('saveMSAL', () => {
		ext.msal.isActive = false;
		ext.msal.activatedCalendars.length = 0;
		if (active) {
			ext.msal.isActive = true;
			calendars.forEach((id) => ext.msal.activatedCalendars.push(id));
			ext.ecosVM.startAgendaSynch(true);
		}
		ext.ecosVM.saveMSAL();
		return true;
	});
}

// nav.js allowDocSynchHandler().
export function setDocSync(allow) {
	const ext = legacyExternis();
	return call('disableDocSync', () => {
		ext.disableDocSync = !allow;
		legacyWindow().cookieHelper.save('disableDocSync', JSON.stringify(ext.disableDocSync));
		if (ext.disableDocSync) legacyModule('storage').clearExtraDoc();
		return true;
	});
}

// nav.js refreshStorage(): storage.size(cb) is a module call, not vmNav - written directly into
// legacyNav.storageSize (setStorageSize(), legacyNavState.js) instead of going through vmNav.storageSize.
export const refreshStorageSize = () => call('refreshStorage', () => legacyModule('storage')?.size(setStorageSize));

// nav.js clearStorage(): no confirmation in the legacy either, straight to the browser APIs + reload.
export function clearLocalStorage() {
	call('clearStorage', () => {
		safe(() => legacyWindow().caches.delete(`externis.appcache.${legacyExternis().version}`));
		safe(() => legacyModule('storage').clear());
		forgetSsoCustomer();
		// The whole application, not only the legacy document: the session is Vue's and was just cleared from storage.
		window.location.reload();
	});
}

// nav.js saveUserExtensions(); mandatory extensions stay enabled.
export function saveExtensions(enabledIds) {
	const ext = legacyExternis();
	const ctx = legacyContext();
	const helper = salesHelper();

	(safe(() => ext.ecosVM.userExtensions, []) ?? []).forEach((ue) =>
		call(`extension ${ue._reference}`, () => {
			const stored = safe(() => JSON.parse(ctx.user.settings.find((s) => s.groupName === 'WEBAPP' && s.name === 'UserExtensions').value));
			if (stored) ue._userSettings = stored.find((item) => item.extensionId === ue.extensionId)?._userSettings || {};

			if (ue._isMandatory || enabledIds.includes(ue.extensionId)) {
				helper.userExtensions.add(ue);
				ext.ecosVM.loadExtensionHook(ue.hook?.extension, ue.hook?.uri);
			} else {
				helper.userExtensions.remove(ue.extensionId);
				ext.ecosVM.removeExtensionHook(ue.hook?.extension);
			}
		})
	);
}

export function openExtensionSettings(extensionId) {
	const ue = safe(() => legacyExternis().ecosVM.userExtensions.find((item) => item.extensionId === extensionId));
	return call('showSettings', () => legacyWindow()[`x_${ue.hook.extension}`].showSettings());
}

export const showSaveSuccess = () => call('showSaveSuccess', () => legacyWindow().showSaveSuccess());

// #endregion

// #region Release notes & support

// home.js loadReleaseNotes(): src/api/core/releaseNotes.js fetches/caches the same static file
// independently of externis.releaseNotes(), which the legacy keeps populating on its own.
export async function readReleaseNotes() {
	const lang = String(safe(() => legacyWindow().getLocalLanguage(), 'fr-FR') || 'fr-FR')
		.split('-')[0]
		.toLowerCase();
	const notes = await getReleaseNotes().catch(() => []);
	return (notes ?? [])
		.map((note, index) => ({ key: index, icon: note?.icon, ...(note?.languages?.[lang] ?? {}) }))
		.filter((note) => note.title?.length > 0)
		.map(({ key, icon, title, description, link }) => ({ key, icon: icon ?? '', title, description: description ?? '', link: link ?? '' }));
}

// nav.js releaseNoteHandler(): discoverLink from the same file src/api/core/releaseNotes.js already fetches.
export async function openAllReleaseNotes() {
	const notes = await getReleaseNotes().catch(() => []);
	if (notes?.[0]?.discoverLink) await openHelp(notes[0].discoverLink);
}

export const SUPPORT_ACTIONS = [
	{ key: 'backupOffline', label: '1) Backup Offline App.', handler: 'backupOfflineHandler' },
	{ key: 'backupDraft', label: '2) Backup & Delete Draft App.', handler: 'backupDraftHandler' },
	{ key: 'backupOnlyDraft', label: '3) Backup Only Draft App.', handler: 'backupOnlyDraftHandler' },
	{ key: 'backupOfflineOrders', label: '4) Backup Offline Orders', handler: 'backupOfflineOrdersHandler' },
];

// nav.js backup*Handler(): each is a bare call to a waSupport module method (already read directly
// elsewhere in this file, executeSupportScript) - only backupOfflineHandler/backupDraftHandler had a
// confirmation swal in nav.js, now Vue's own.
const BACKUP_METHODS = { backupOfflineHandler: 'backupOfflineAppointments', backupDraftHandler: 'backupDraftAppointments', backupOnlyDraftHandler: 'backupOnlyDraftAppointments', backupOfflineOrdersHandler: 'backupOfflineOrders' };
const CONFIRMED_BACKUPS = new Set(['backupOfflineHandler', 'backupDraftHandler']);

export function runSupportAction(action) {
	const method = BACKUP_METHODS[action.handler];
	const run = () => call(action.handler, () => legacyModule('waSupport')[method](() => setTimeout(() => safe(() => legacyWindow().showOperationComplete()), 500)), SUPPORT_HOLD_MS);
	if (!CONFIRMED_BACKUPS.has(action.handler)) return run();
	confirmAction({ title: t('Support_Confirm'), lines: [t('Support_Confirm_Backup_false')], icon: 'warning', confirmText: t('Yes'), cancelText: t('Cancel'), run });
}

export const executeSupportScript = (scriptId) => scriptId && call('waSupport.loadScript', () => legacyModule('waSupport').loadScript(scriptId), SUPPORT_HOLD_MS);

// #endregion

export { refreshLegacyNav };
